import * as React from 'react';

import { computeText } from '@/locales/utils';
import { Input } from '@/components/form/Input';
import { FormattedMessage, useIntl } from 'react-intl';
import { Text } from '@/components/atoms/Text';
import { SvyButton } from '@/components/savory/SvyButton';
import { confirm } from '@/core/asyncActions/confirm';
import { useEditCustomerLoyaltiesMutation } from '@/services/innovorder/adminTools/adminTools.endpoints';
import { FormControl, FormControls, SuccessMessage } from '../AdminTools.style';
import { safeMutate } from '@/utils/errors';
import { Form } from '@/core/form/Form';
import { useParams } from 'react-router';

export type EditCustomerLoyaltiesInputs = {
    emailsOrCards: string;
    loyaltyAmountToAdd: number;
};

interface ParamTypes {
    brandId: string;
}

const EditCustomerLoyalties: React.FunctionComponent = () => {
    const intl = useIntl();

    const [editCustomerLoyalties, { isSuccess }] = useEditCustomerLoyaltiesMutation();

    const { brandId } = useParams<ParamTypes>();

    const handleSubmitForm = async ({ emailsOrCards, loyaltyAmountToAdd }: EditCustomerLoyaltiesInputs) => {
        const hasConfirmed = await confirm(computeText(intl, 'adminTools.confirmModal.description'));
        if (hasConfirmed) {
            await safeMutate(
                editCustomerLoyalties({
                    brandId: parseInt(brandId),
                    emailsOrCards,
                    loyaltyAmountToAdd,
                }),
            );
        }
    };

    return (
        <Form<EditCustomerLoyaltiesInputs>>
            {({ handleSubmit }) => {
                return (
                    <>
                        <Text type="large" text="brand.adminTools.editCustomerLoyalties.title" />
                        <FormControls>
                            <FormControl>
                                <Input
                                    name="emailsOrCards"
                                    rules={{ required: computeText(intl, 'field.required') }}
                                    placeholder="brand.adminTools.editCustomerLoyalties.form.placeholder.emailsOrCards"
                                    labelId="brand.adminTools.editCustomerLoyalties.form.emailsOrCards"
                                />
                            </FormControl>
                            <FormControl>
                                <Input
                                    name="loyaltyAmountToAdd"
                                    rules={{ required: computeText(intl, 'field.required') }}
                                    placeholder="brand.adminTools.editCustomerLoyalties.form.placeholder.loyaltyAmountToAdd"
                                    labelId="brand.adminTools.editCustomerLoyalties.form.loyaltyAmountToAdd"
                                />
                            </FormControl>
                            <SvyButton onClick={handleSubmit(handleSubmitForm)}>
                                <FormattedMessage id="button.validate" />
                            </SvyButton>
                            {isSuccess && <SuccessMessage text="brand.adminTools.editCustomerLoyalties.form.success" />}
                        </FormControls>
                    </>
                );
            }}
        </Form>
    );
};

export default EditCustomerLoyalties;
